import { applyStaticTranslations, i18n, type MessageKey } from "./i18n";
import { fitToStops } from "./map";
import { openSummaryDialog } from "./summary";

type ShortcutKey = "search" | "fit" | "summary" | "help" | "close";

const IS_MAC = /Mac|iPhone|iPod|iPad/.test(navigator.platform);
const MOD_KEY = IS_MAC ? "⌘" : "Ctrl";

const SHORTCUT_LABELS: Record<ShortcutKey, { keys: string[]; labelKey: MessageKey }> = {
  search: { keys: [`${MOD_KEY} K`, "/"], labelKey: "shortcutsSearch" },
  fit: { keys: ["F"], labelKey: "shortcutsFit" },
  summary: { keys: ["S"], labelKey: "shortcutsSummary" },
  help: { keys: ["?"], labelKey: "shortcutsHelp" },
  close: { keys: ["Esc"], labelKey: "shortcutsCloseRow" },
};

let dialog: HTMLDialogElement | null = null;

export function setupShortcuts(): void {
  dialog = document.querySelector<HTMLDialogElement>("#shortcuts-dialog");
  if (dialog) {
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) dialog?.close();
    });
    const closeBtn = dialog.querySelector<HTMLButtonElement>("[data-shortcuts-close]");
    closeBtn?.addEventListener("click", () => dialog?.close());
  }

  renderShortcutsList();
  i18n.addEventListener("change", renderShortcutsList);

  document.addEventListener("keydown", onKeydown);
}

function onKeydown(e: KeyboardEvent): void {
  if (isTypingTarget(e.target)) return;

  const primaryMod = IS_MAC ? e.metaKey : e.ctrlKey;
  if (primaryMod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
    e.preventDefault();
    focusSearch();
    return;
  }

  if (e.metaKey || e.ctrlKey || e.altKey) return;

  if (e.key === "/") {
    e.preventDefault();
    focusSearch();
    return;
  }

  if (e.key === "?") {
    e.preventDefault();
    openShortcutsDialog();
    return;
  }

  const lower = e.key.toLowerCase();
  if (lower === "f") {
    e.preventDefault();
    fitToStops();
    return;
  }
  if (lower === "s") {
    e.preventDefault();
    openSummaryDialog();
    return;
  }
}

function focusSearch(): void {
  const search = document.querySelector<HTMLInputElement>("#search-input");
  if (!search) return;
  if (window.innerWidth < 900) {
    document.body.classList.add("sidebar-open");
    const toggle = document.querySelector<HTMLButtonElement>("#sidebar-toggle");
    const scrim = document.querySelector<HTMLElement>("#sidebar-scrim");
    toggle?.setAttribute("aria-expanded", "true");
    if (scrim) scrim.hidden = false;
  }
  search.focus();
  search.select();
}

export function openShortcutsDialog(): void {
  if (!dialog) return;
  applyStaticTranslations(dialog);
  if (!dialog.open) dialog.showModal();
}

function renderShortcutsList(): void {
  const list = document.querySelector<HTMLElement>("#shortcuts-list");
  if (!list) return;
  list.replaceChildren();
  for (const id of ["search", "fit", "summary", "help", "close"] as ShortcutKey[]) {
    const { keys, labelKey } = SHORTCUT_LABELS[id];
    const row = document.createElement("div");
    row.className = "shortcut-row";
    const keyGroup = document.createElement("span");
    keyGroup.className = "shortcut-keys";
    keys.forEach((k, i) => {
      if (i > 0) {
        const sep = document.createElement("span");
        sep.className = "shortcut-or";
        sep.textContent = "/";
        keyGroup.appendChild(sep);
      }
      const kbd = document.createElement("kbd");
      kbd.textContent = k;
      keyGroup.appendChild(kbd);
    });
    const label = document.createElement("span");
    label.textContent = i18n.t(labelKey);
    row.append(keyGroup, label);
    list.appendChild(row);
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}
